import React, { useState, useEffect } from 'react';
import { getOrders, getMotoboys, getAnalysisData } from '../services/apiServices';
import styles from './Reports.module.css';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const getYesterdayDateString = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return yesterday.toISOString().slice(0, 10);
};

const Reports = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [motoboys, setMotoboys] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  // Modificado para suportar dois períodos de análise
  const [analysisData1, setAnalysisData1] = useState(null);
  const [analysisData2, setAnalysisData2] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedMotoboy, setSelectedMotoboy] = useState('all');
  const [activeView, setActiveView] = useState('deliveries'); // 2. Estado para controlar a visão
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // MODIFICAÇÃO: A busca de dados agora depende da data selecionada.
    const fetchData = async () => {
      setLoading(true);
      try {
        // Passa a data selecionada para a API. Se a data for vazia, a API pode buscar os de hoje.
        const ordersData = await getOrders({ date: selectedDate });
        const motoboysData = await getMotoboys();
        const all = [...ordersData.delivery, ...ordersData.pickup, ...(ordersData.completed || [])];
        setAllOrders(all);
        setFilteredOrders(all); // Inicialmente, os pedidos filtrados são todos os que vieram da API
        setMotoboys(motoboysData);
      } catch (error) {
        console.error("Erro ao buscar dados para relatórios:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedDate]); // A busca é refeita quando a data muda.

  useEffect(() => {
    let tempOrders = allOrders;

    // Filtra por motoboy
    if (selectedMotoboy !== 'all') {
      tempOrders = tempOrders.filter(order => order.motoboyId === selectedMotoboy);
    }

    setFilteredOrders(tempOrders);
  }, [selectedDate, selectedMotoboy, allOrders]);

  // Filtros para a aba de análise
  const [period1, setPeriod1] = useState({ startDate: getYesterdayDateString(), endDate: getYesterdayDateString() });
  const [period2, setPeriod2] = useState({ startDate: '', endDate: '' });
  const [compare, setCompare] = useState(false);

  // Função para lidar com a mudança no checkbox de comparação
  const handleCompareToggle = () => {
    const newCompareState = !compare;
    setCompare(newCompareState);

    // MELHORIA: Se o usuário está ativando a comparação e o período 2 está vazio,
    // preenchemos com uma data padrão para facilitar.
    if (newCompareState && !period2.startDate && period1.startDate) {
      const period1StartDate = new Date(period1.startDate);
      const dayBefore = new Date(period1StartDate);
      dayBefore.setDate(period1StartDate.getDate() - 1);
      setPeriod2({ startDate: dayBefore.toISOString().slice(0, 10), endDate: dayBefore.toISOString().slice(0, 10) });
    }
  };

  const handleGenerateAnalysis = async () => {
    setAnalysisLoading(true);
    setAnalysisError(null);
    setAnalysisData1(null);
    setAnalysisData2(null);

    try {
      // Busca dados para o primeiro período
      const data1 = await getAnalysisData({
        startDate: period1.startDate,
        endDate: period1.endDate,
      });
      setAnalysisData1(data1);

      // Se a comparação estiver ativa, busca dados para o segundo período
      if (compare && period2.startDate && period2.endDate) {
        const data2 = await getAnalysisData({
          startDate: period2.startDate,
          endDate: period2.endDate,
        });
        setAnalysisData2(data2);
      }

    } catch (err) {
      setAnalysisError('Falha ao carregar a análise. Tente novamente mais tarde.');
      console.error(err);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const totalDeliveries = filteredOrders.filter(o => o.deliveryType === 'delivery').length;
  const totalValue = filteredOrders.reduce((acc, order) => acc + order.total, 0);
  const totalCash = filteredOrders.filter(o => o.paymentMethod.toLowerCase().includes('dinheiro')).reduce((acc, order) => acc + order.total, 0);

  // Preparação de dados para o gráfico de Pizza (Vendas por Pagamento)
  const paymentMethodsData = filteredOrders.reduce((acc, order) => {
    let method = 'Outros';
    if (order.paymentMethod && order.paymentMethod.toLowerCase().includes('pix')) method = 'Pix';
    else if (order.paymentMethod && order.paymentMethod.toLowerCase().includes('dinheiro')) method = 'Dinheiro';
    else if (order.paymentMethod && order.paymentMethod.toLowerCase().includes('cartão')) method = 'Cartão';

    const existing = acc.find(item => item.name === method);
    if (existing) {
      existing.value += order.total;
    } else {
      acc.push({ name: method, value: order.total });
    }
    return acc;
  }, []);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <div className={styles.container} style={{ width: '100%', height: '100%', overflowY: 'auto' }}>
      <div className={styles.header}>
        <h2>Relatórios</h2>
        <div className={styles.viewSelector}>
          <button
            className={activeView === 'deliveries' ? styles.active : ''}
            onClick={() => setActiveView('deliveries')}
          >
            Relatório de Entregas
          </button>
          <button
            className={activeView === 'analysis' ? styles.active : ''}
            onClick={() => setActiveView('analysis')}
          >
            Análise de Dados
          </button>
        </div>
      </div>

      {activeView === 'deliveries' && (
        <>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label>Data:</label>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            </div>
            <div className={styles.filterGroup}>
              <label>Motoboy:</label>
              <select value={selectedMotoboy} onChange={(e) => setSelectedMotoboy(e.target.value)}>
                <option value="all">Todos</option>
                {motoboys.map(mb => (
                  <option key={mb.id} value={mb.id}>{mb.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? <p>Carregando...</p> : (
            <>
              <div className={styles.summary}>
                <div className={styles.summaryCard}><h4>Total de Entregas</h4><p>{totalDeliveries}</p></div>
                <div className={styles.summaryCard}><h4>Valor Total</h4><p>R$ {totalValue.toFixed(2)}</p></div>
                <div className={styles.summaryCard}><h4>Total em Dinheiro</h4><p>R$ {totalCash.toFixed(2)}</p></div>
              </div>

              {/* Gráfico de Vendas por Pagamento */}
              <div className={styles.chartContainer} style={{ height: '300px', marginBottom: '30px', backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Vendas por Método de Pagamento</h3>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentMethodsData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentMethodsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `R$ ${value.toFixed(2)}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.reportTable}>
                  <thead><tr><th>Pedido Nº</th><th>Cliente</th><th>Valor</th><th>Pagamento</th></tr></thead>
                  <tbody>
                    {filteredOrders.filter(o => o.deliveryType === 'delivery').map(order => (
                      <tr key={order.id}>
                        <td>{order.orderNumber}</td>
                        <td>{order.customerName}</td>
                        <td>R$ {order.total.toFixed(2)}</td>
                        <td>{order.paymentMethod}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {activeView === 'analysis' && (
        <div className={styles.analysisContainer}>
          <div className={styles.analysisFilters}>
            <div className={styles.periodContainer}>
              <strong>Período 1</strong>
              <div className={styles.filterGroup}>
                <label>Data de Início:</label>
                <input type="date" value={period1.startDate} onChange={(e) => setPeriod1({ ...period1, startDate: e.target.value })} />
              </div>
              <div className={styles.filterGroup}>
                <label>Data de Fim:</label>
                <input type="date" value={period1.endDate} onChange={(e) => setPeriod1({ ...period1, endDate: e.target.value })} />
              </div>
            </div>

            {compare && (
              <div className={styles.periodContainer}>
                <strong>Período 2</strong>
                <div className={styles.filterGroup}>
                  <label>Data de Início:</label>
                  <input type="date" value={period2.startDate} onChange={(e) => setPeriod2({ ...period2, startDate: e.target.value })} />
                </div>
                <div className={styles.filterGroup}>
                  <label>Data de Fim:</label>
                  <input type="date" value={period2.endDate} onChange={(e) => setPeriod2({ ...period2, endDate: e.target.value })} />
                </div>
              </div>
            )}

            <div className={styles.actionButtons}>
              <label className={styles.compareLabel}>
                <input type="checkbox" checked={compare} onChange={handleCompareToggle} />
                Comparar Períodos
              </label>
              <button onClick={handleGenerateAnalysis} disabled={analysisLoading} className={styles.generateBtn}>
                {analysisLoading ? 'Gerando...' : 'Gerar Análise'}
              </button>
            </div>
          </div>

          {analysisLoading && <p>Gerando análise, por favor aguarde...</p>}
          {analysisError && <p className={styles.error}>{analysisError}</p>}
          
          <div className={styles.analysisResultsGrid}>
            {analysisData1 && <AnalysisResultColumn data={analysisData1} title="Período 1" />}
            {analysisData2 && <AnalysisResultColumn data={analysisData2} title="Período 2" />}
          </div>
        </div>
      )}
    </div>
  );
};

// Função auxiliar para formatar minutos em "Xh Ym Zs"
const formatDuration = (minutesVal) => {
  const minutes = Number(minutesVal);
  if (isNaN(minutes)) return '0s';
  
  const totalSeconds = Math.floor(minutes * 60);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

// Componente auxiliar para renderizar uma coluna de resultados
const AnalysisResultColumn = ({ data, title }) => {
  const { metadata, peakTime, orderTimes, faq, customerMetrics, outOfStock } = data;

  const peakTimeData = peakTime.interactionsByHour.map((count, hour) => ({
    name: `${String(hour).padStart(2, '0')}h`,
    interacoes: count
  }));

  return (
    <div className={styles.analysisResults}>
      <h3>{title}: {new Date(metadata.analysisPeriod.start).toLocaleDateString('pt-BR')} a {new Date(metadata.analysisPeriod.end).toLocaleDateString('pt-BR')}</h3>
      
      <div className={styles.card}>
        <h4>Métricas de Clientes (via Bot)</h4>
        <p><strong>Taxa de Conversão:</strong> {customerMetrics.conversionRate}%</p>
        <p>Clientes com interação: {customerMetrics.totalInteractions}</p>
        <p>Clientes que fizeram pedido: {customerMetrics.totalOrdersFromBot}</p>
        <p>Clientes que não finalizaram: {customerMetrics.totalDropOffs}</p>
      </div>

      <div className={styles.card}>
        <h4>Respostas "Sem Assados"</h4>
        <p>O bot informou falta de assados para <strong>{outOfStock.count}</strong> clientes.</p>
      </div>

      <div className={styles.card}>
        <h4>Horários de Pico (Interações)</h4>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={peakTimeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="interacoes" name="Interações" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={styles.card}>
        <h4>Tempo para Concluir Pedido</h4>
        {orderTimes.totalAnalyzed > 0 ? (
          <>
            <p><strong>Tempo Médio:</strong> {formatDuration(orderTimes.averageTime)}</p>
            <p><strong>Pedido Mais Rápido:</strong> {formatDuration(orderTimes.fastestOrder.duration)} ({orderTimes.fastestOrder.name})</p>
            <p><strong>Pedido Mais Lento:</strong> {formatDuration(orderTimes.slowestOrder.duration)} ({orderTimes.slowestOrder.name})</p>
          </>
        ) : ( <p>{orderTimes.message}</p> )}
      </div>

      <div className={styles.card}>
        <h4>Top 5 Clientes (Todos os Pedidos)</h4>
        {customerMetrics.topCustomers.length > 0 ? (
          <ul>
            {customerMetrics.topCustomers.map(c => ( <li key={c.name}>{c.name}: <strong>{c.count}</strong> pedido(s)</li> ))}
          </ul>
        ) : ( <p>Nenhum pedido registrado.</p> )}
      </div>

      <div className={styles.card}>
        <h4>Dúvidas Frequentes (FAQ)</h4>
        {faq.questions.length > 0 ? (
          <>
            <p>Foram registradas {faq.questions.length} dúvidas:</p>
            <ul>{faq.questions.slice(0, 5).map((q, i) => ( <li key={i}>"{q}"</li> ))}</ul>
            {faq.questions.length > 5 && <p>... e mais.</p>}
          </>
        ) : ( <p>Nenhuma dúvida foi registrada pelo botão de FAQ.</p> )}
      </div>
    </div>
  );
};

export default Reports;